import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Beaker, FileCheck2, CheckCircle2, Circle, Clock, ChevronDown, ChevronUp } from "lucide-react";

const TYPE_ICON = { reading: BookOpen, lab: Beaker, exam: FileCheck2 };
const TYPE_COLOR = { reading: "var(--cyan)", lab: "var(--amber)", exam: "var(--green)" };

// Tactical markdown renderers — keep palette and monospace voice
const MD_COMPONENTS = {
  h1: (p) => <h1 className="font-display text-2xl uppercase tracking-tight text-white mt-4 mb-2" {...p} />,
  h2: (p) => <h2 className="font-display text-xl uppercase tracking-tight text-[var(--cyan)] mt-4 mb-2" {...p} />,
  h3: (p) => <h3 className="font-display text-base uppercase tracking-wider text-[var(--amber)] mt-3 mb-1" {...p} />,
  p:  (p) => <p className="text-[var(--text)] text-sm leading-relaxed mb-3" {...p} />,
  ul: (p) => <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-[var(--text-2)] mb-3" {...p} />,
  ol: (p) => <ol className="list-decimal list-outside ml-5 space-y-1 text-sm text-[var(--text-2)] mb-3" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-[var(--cyan)] pl-3 italic text-[var(--text-2)] text-sm my-3" {...p} />,
  code: ({ inline, children, ...rest }) => inline
    ? <code className="font-mono text-xs bg-[var(--surface-2)] text-[var(--cyan)] px-1.5 py-0.5 rounded-sm" {...rest}>{children}</code>
    : <pre className="font-mono text-xs bg-[var(--surface-2)] text-[var(--text)] border border-[var(--border)] p-3 my-3 overflow-x-auto"><code {...rest}>{children}</code></pre>,
  a: (p) => <a className="text-[var(--cyan)] underline hover:text-white" target="_blank" rel="noreferrer" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  em: (p) => <em className="text-[var(--text-2)]" {...p} />,
  hr: () => <hr className="border-[var(--border)] my-4" />,
};

function previewText(content) {
  if (!content) return "";
  const stripped = content.replace(/[#>*_`]/g, "").trim();
  return stripped.split("\n")[0].slice(0, 110);
}

export default function LessonRow({ moduleId, lesson, lessonIdx, modIdx, isDone, isBusy, onToggle }) {
  const Icon = TYPE_ICON[lesson.type] || BookOpen;
  const [open, setOpen] = useState(false);
  const hasMaterial = !!(lesson.content && lesson.content.trim());

  return (
    <div className="px-5 py-4 hover:bg-[var(--surface-2)] transition-colors" data-testid={`lesson-${modIdx + 1}-${lessonIdx + 1}`}>
      <div className="flex items-start gap-4">
        <button
          onClick={() => onToggle(moduleId, lesson)}
          disabled={isBusy}
          className="mt-0.5 shrink-0"
          data-testid={`lesson-toggle-${modIdx + 1}-${lessonIdx + 1}`}
          aria-label={isDone ? "Mark incomplete" : "Mark complete"}
        >
          {isDone
            ? <CheckCircle2 size={20} strokeWidth={1.5} color="var(--green)" />
            : <Circle size={20} strokeWidth={1.5} color="var(--text-2)" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={14} strokeWidth={1.5} color={TYPE_COLOR[lesson.type] || "var(--cyan)"} />
            <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: TYPE_COLOR[lesson.type] }}>{lesson.type}</span>
            <span className="font-mono text-[10px] text-[var(--text-2)] inline-flex items-center gap-1">
              <Clock size={10} /> {lesson.duration_min}m
            </span>
          </div>
          <div className={`font-display text-lg ${isDone ? "text-[var(--text-2)] line-through" : "text-white"}`}>
            {lesson.title}
          </div>
          {hasMaterial && !open && (
            <p className="text-sm text-[var(--text-2)] mt-1 truncate">{previewText(lesson.content)}…</p>
          )}
          {hasMaterial && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[var(--cyan)] hover:text-white inline-flex items-center gap-1"
              data-testid={`lesson-view-${modIdx + 1}-${lessonIdx + 1}`}
            >
              {open ? <><ChevronUp size={12} /> Hide material</> : <><ChevronDown size={12} /> View material</>}
            </button>
          )}
          {open && hasMaterial && (
            <div
              className="mt-3 border-l-2 border-[var(--cyan)] pl-4 py-1"
              data-testid={`lesson-content-${modIdx + 1}-${lessonIdx + 1}`}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {lesson.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
